import React, { useEffect, useRef } from 'react';
import { Phone, PhoneOff, Video, Maximize2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { Avatar } from '../common';

/**
 * Play a WhatsApp-style ring tone via Web Audio (no asset needed).
 * Same pattern as before — plays through tab minimize.
 */
const playRingtone = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return () => {};
  let ctx;
  try {
    ctx = new AudioCtx();
  } catch { return () => {}; }

  let stopped = false;
  let interval;

  const playBeep = (freq, startOffset, duration) => {
    if (stopped) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const t = ctx.currentTime + startOffset;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
    gain.gain.linearRampToValueAtTime(0, t + duration);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  };

  const tick = () => {
    playBeep(880, 0, 0.35);
    playBeep(660, 0.45, 0.35);
  };

  tick();
  interval = setInterval(tick, 2000);

  return () => {
    stopped = true;
    clearInterval(interval);
    try { ctx.close(); } catch {}
  };
};

const showSystemNotification = (caller, pricingMode, price, freeCallLabel, titleLabel) => {
  if (typeof Notification === 'undefined') return null;
  if (Notification.permission !== 'granted') return null;

  const priceLabel =
    pricingMode === 'free' ? freeCallLabel :
    pricingMode === 'per_minute' ? `$${Number(price).toFixed(2)}/min` :
    `$${Number(price).toFixed(2)}`;

  try {
    const n = new Notification(titleLabel, {
      body: `${caller?.display_name || caller?.username || 'Someone'} · ${priceLabel}`,
      icon: caller?.avatar || '/favicon.ico',
      tag: 'incoming-call',
      requireInteraction: true,
    });
    n.onclick = () => { window.focus(); n.close(); };
    return n;
  } catch {
    return null;
  }
};

/**
 * Sticky bottom-right toast shown while a call is in the 'requested' state.
 * Replaces the disruptive full-screen modal as the primary ringing UI.
 *   - Caller side:  "Calling @X…" + Cancel button + Expand to fullscreen
 *   - Recipient side: "Incoming call from @X" + Accept + Decline + Expand
 */
const CallToast = () => {
  const { t } = useTranslation('livestream');
  const {
    currentCall,
    acceptCall,
    declineCall,
    openFullscreen,
    loading,
  } = useVideoCall();

  const stopRingtoneRef = useRef(null);
  const notificationRef = useRef(null);

  // Toast is the RECIPIENT's primary ringing UI. Caller lands in the
  // full-screen OutgoingCallModal (auto-opened) so they always see a big
  // Cancel button + avatar of who they're calling. If the caller minimizes,
  // the chat bubble becomes the way back.
  const isRecipient = currentCall
    && currentCall.status === 'requested'
    && currentCall.my_role === 'recipient';
  const show = isRecipient;

  // Ringtone + OS notification only for the recipient of an incoming call
  useEffect(() => {
    if (!isRecipient) return;

    stopRingtoneRef.current = playRingtone();
    notificationRef.current = showSystemNotification(
      currentCall.caller,
      currentCall.pricing_mode,
      currentCall.price,
      t('videoCall.common.freeCall'),
      t('videoCall.incoming.title')
    );

    return () => {
      if (stopRingtoneRef.current) { stopRingtoneRef.current(); stopRingtoneRef.current = null; }
      if (notificationRef.current) { try { notificationRef.current.close(); } catch {} notificationRef.current = null; }
    };
  }, [isRecipient, currentCall?.caller, currentCall?.pricing_mode, currentCall?.price, t]);

  if (!show) return null;

  const caller = currentCall.caller;

  const priceLabel =
    currentCall.pricing_mode === 'free' ? t('videoCall.common.freeCall') :
    currentCall.pricing_mode === 'per_minute'
      ? `$${Number(currentCall.price).toFixed(2)}/min`
      : `$${Number(currentCall.price).toFixed(2)}`;

  return (
    <div
      className="
        fixed z-[150] rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-neutral-700
        bottom-4 left-4 right-4
        sm:left-auto sm:right-4 sm:w-[360px]
      "
      style={{ background: 'var(--color-primary-50, #fdf2f8)' }}
    >
      <div
        className="p-4"
        style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-primary-700))' }}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <div className="relative">
              <Avatar user={caller} size="md" />
            </div>
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-80 font-semibold mb-0.5">
              <Video className="w-3 h-3" />
              {t('videoCall.toast.incomingCall')}
            </div>
            <p className="text-sm font-semibold truncate">{caller?.display_name || caller?.username}</p>
            <p className="text-[11px] opacity-80 truncate">{priceLabel}</p>
          </div>
          <button
            onClick={openFullscreen}
            title={t('videoCall.toast.openFullScreen')}
            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-3 flex items-center gap-2 bg-white dark:bg-neutral-900">
        <button
          onClick={() => declineCall(currentCall.id)}
          disabled={loading}
          className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          <PhoneOff className="w-4 h-4" />
          {t('videoCall.common.decline')}
        </button>
        <button
          onClick={() => acceptCall(currentCall.id)}
          disabled={loading}
          className="flex-1 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
          {t('videoCall.common.accept')}
        </button>
      </div>
    </div>
  );
};

export default CallToast;
